import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import { TextArea, TextField } from '../ui/Field';

export interface WorkflowRunFormValue {
  message: string;
  conversationId?: string;
  clientId?: string;
  userTenantProfileId?: string;
}

interface WorkflowRunModalProps {
  open: boolean;
  workflowName: string;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (value: WorkflowRunFormValue) => void;
}

const initialFormState: WorkflowRunFormValue = {
  message: 'I need help',
  conversationId: '',
  clientId: '',
  userTenantProfileId: '',
};

export const WorkflowRunModal: React.FC<WorkflowRunModalProps> = ({
  open,
  workflowName,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState<WorkflowRunFormValue>(initialFormState);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initialFormState);
    setShowAdvanced(false);
  }, [open]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.message.trim()) return;

    onSubmit({
      message: form.message.trim(),
      conversationId: form.conversationId?.trim() || undefined,
      clientId: form.clientId?.trim() || undefined,
      userTenantProfileId: form.userTenantProfileId?.trim() || undefined,
    });
  };

  return (
    <Modal
      isOpen={open}
      onClose={() => !loading && onClose()}
      title={`Run Workflow: ${workflowName}`}
      size="md"
      footer={
        <>
          <Button tone="custom"
            type="button"
            className="rounded-md border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button tone="custom"
            type="submit"
            form="workflow-run-form"
            className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={loading || !form.message.trim()}
          >
            {loading ? 'Starting…' : 'Start Run'}
          </Button>
        </>
      }
    >
      <form id="workflow-run-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
          Start a manual run for this workflow by providing the input message that should enter the execution path.
        </div>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700">Message</span>
          <TextArea tone="focus-blue"
            className="min-h-[120px] w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2"
            value={form.message}
            onChange={(event) => setForm((prev) => ({ ...prev, message: event.target.value }))}
            placeholder="Describe the user input to run through the workflow"
            disabled={loading}
          />
        </label>

        <div className="border-t border-gray-100 pt-2">
          <Button tone="link"
            type="button"
            className="text-sm font-medium"
            onClick={() => setShowAdvanced((prev) => !prev)}
            disabled={loading}
          >
            {showAdvanced ? 'Hide advanced fields' : 'Show advanced fields'}
          </Button>
        </div>

        {showAdvanced ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Conversation ID</span>
              <TextField tone="focus-blue"
                className="w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2"
                value={form.conversationId}
                onChange={(event) => setForm((prev) => ({ ...prev, conversationId: event.target.value }))}
                placeholder="Optional"
                disabled={loading}
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Client ID</span>
              <TextField tone="focus-blue"
                className="w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2"
                value={form.clientId}
                onChange={(event) => setForm((prev) => ({ ...prev, clientId: event.target.value }))}
                placeholder="Optional"
                disabled={loading}
              />
            </label>

            <label className="block md:col-span-2">
              <span className="mb-1 block text-sm font-medium text-gray-700">User Tenant Profile ID</span>
              <TextField tone="focus-blue"
                className="w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2"
                value={form.userTenantProfileId}
                onChange={(event) => setForm((prev) => ({ ...prev, userTenantProfileId: event.target.value }))}
                placeholder="Optional"
                disabled={loading}
              />
            </label>
          </div>
        ) : null}
      </form>
    </Modal>
  );
};

export default WorkflowRunModal;
